import React, { useState } from 'react';
import { noop } from 'lodash';

import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import Typography from '~/components/core/Atomic/Typography';
import { ChevronUpIcon } from '~/components/icons';
import type { CollapseProps } from '~/components/ui/atoms/Collapse';
import Collapse from '~/components/ui/atoms/Collapse';
import cn from '~/Utils/cn';

import styles from './Collapsible.module.scss';

const EXPANSION_MODES = { drawer: 'drawer', grow: 'grow' } as const;

const VARIANT = {
  default: 'default',
  slim: 'slim',
} as const;

const TITLE_COLOR = {
  buttonLink: 'buttonLink',
  textPrimary: 'textPrimary',
  error: 'error',
} as const;

const BACKGROUND_COLOR = {
  WHITE: 'WHITE',
  LIST: 'LIST',
} as const;

const BORDER_COLOR = {
  GREY: 'GREY',
  HIGHLIGHT: 'HIGHLIGHT',
} as const;

const CHEVRON_SIDE = {
  LEFT: 'LEFT',
  RIGHT: 'RIGHT',
} as const;

const COLLAPSIBLE_SUMMARY_POSITION = {
  TOP: 'TOP',
  BOTTOM: 'BOTTOM',
} as const;

type ExpansionMode = (typeof EXPANSION_MODES)[keyof typeof EXPANSION_MODES];
type VariantType = (typeof VARIANT)[keyof typeof VARIANT];
type TitleColorType = (typeof TITLE_COLOR)[keyof typeof TITLE_COLOR];
type BackgroundColorType = (typeof BACKGROUND_COLOR)[keyof typeof BACKGROUND_COLOR];
type BorderColorType = (typeof BORDER_COLOR)[keyof typeof BORDER_COLOR];
type ChevronSideType = (typeof CHEVRON_SIDE)[keyof typeof CHEVRON_SIDE];
type CollapsibleSummaryPositionType = (typeof COLLAPSIBLE_SUMMARY_POSITION)[keyof typeof COLLAPSIBLE_SUMMARY_POSITION];

interface CollapsibleWrapperProps {
  className?: string;
  title: string | React.ReactNode;
  titleTag?: React.ReactNode;
  subtitle?: string;
  children: React.ReactNode;
  defaultState?: boolean;
  onCollapse?: (isOpen: boolean) => void;
  expansionMode?: ExpansionMode;
  actionCard?: boolean;
  noBorder?: boolean;
  noBackground?: boolean;
  noHorizontalPadding?: boolean;
  disabled?: boolean;
  isCardShadow?: boolean;
  variant?: VariantType;
  titleColor?: TitleColorType;
  noBorderLine?: boolean;
  backgroundColor?: BackgroundColorType;
  borderColor?: BorderColorType;
  noDivider?: boolean;
  actions?: React.ReactNode;
  openOnChevronClickOnly?: boolean;
  open?: boolean;
  isCollapsible?: boolean;
  unmountOnExit?: boolean;
  additionalHeaderContent?: React.ReactNode;
  summarySectionClassname?: string;
  isActionCardDrawer?: boolean;
  additionalSubtitleOnlyWhenOpen?: string;
  withActionsContainerFullWidth?: boolean;
  hideActionsOnCollapse?: boolean;
  chevronSide?: ChevronSideType;
  disabledWithoutPadding?: boolean;
  disabledWithoutChevron?: boolean;
  centerCollapsibleSummary?: boolean;
  collapsibleSummaryPosition?: CollapsibleSummaryPositionType;
  chevronContainerClassName?: string;
  timeout?: CollapseProps['timeout'];
}

const variantClassNames = {
  container: {
    [VARIANT.default]: 'containerDefault',
    [VARIANT.slim]: 'containerSlim',
  },
  title: {
    [VARIANT.default]: 'titleDefault',
    [VARIANT.slim]: 'titleSlim',
  },
  subTitle: {
    [VARIANT.default]: 'subtitleDefault',
    [VARIANT.slim]: 'subtitleSlim',
  },
};

const titleColorClassNames = {
  [TITLE_COLOR.buttonLink]: 'titleColorButtonLink',
  [TITLE_COLOR.textPrimary]: 'titleColorTextPrimary',
  [TITLE_COLOR.error]: 'titleColorError',
};

const backgroundColorClassNames = {
  [BACKGROUND_COLOR.WHITE]: 'backgroundWhite',
  [BACKGROUND_COLOR.LIST]: 'backgroundList',
};

const borderColorClassNames = {
  [BORDER_COLOR.GREY]: 'borderGrey',
  [BORDER_COLOR.HIGHLIGHT]: 'borderHighlight',
};

// `overflow-wrap` is inherited, so `wrap-anywhere` may only go on an element whose whole subtree is
// plain text. A slot typed `string` today can be widened to a node later; checking the value rather
// than trusting the prop type keeps the wrap from silently following it onto consumer JSX.
const isPlainText = (value: React.ReactNode): boolean =>
  value === undefined || value === null || typeof value === 'string';

// Names the chevron button when the title is not plain text and so cannot name it.
const TOGGLE_FALLBACK_LABEL = 'Toggle section';

const ChevronContainer = ({
  isCollapsible,
  isExpanded,
  label,
  disabled,
  onClick,
  className,
}: {
  isCollapsible: boolean;
  isExpanded: boolean;
  label: string;
  disabled?: boolean;
  onClick: () => void;
  className?: string;
}) => (
  <div className={className}>
    {isCollapsible ? (
      <IconButton
        disabled={disabled}
        size="small"
        className="h-24 w-24 p-0"
        aria-label={label}
        aria-expanded={isExpanded}
        onClick={(event) => {
          event.stopPropagation();
          onClick();
        }}
      >
        <ChevronUpIcon iconColor="currentColor" size={22} className={cn({ 'rotate-180': !isExpanded })} />
      </IconButton>
    ) : null}
  </div>
);

const CollapsibleWrapper = ({
  className,
  title,
  titleTag,
  isCardShadow = false,
  subtitle,
  children,
  defaultState = false,
  onCollapse = noop,
  expansionMode = EXPANSION_MODES.drawer,
  actionCard = false,
  noBorder = false,
  noBackground = false,
  noHorizontalPadding = false,
  disabled = false,
  variant = VARIANT.default,
  titleColor = TITLE_COLOR.textPrimary,
  noBorderLine = false,
  backgroundColor,
  borderColor = BORDER_COLOR.GREY,
  noDivider = false,
  actions,
  openOnChevronClickOnly = false,
  open,
  isCollapsible = true,
  unmountOnExit = true,
  additionalHeaderContent,
  summarySectionClassname,
  isActionCardDrawer = true,
  additionalSubtitleOnlyWhenOpen,
  withActionsContainerFullWidth = true,
  hideActionsOnCollapse = false,
  chevronSide = CHEVRON_SIDE.LEFT,
  disabledWithoutPadding = false,
  disabledWithoutChevron = false,
  centerCollapsibleSummary = false,
  collapsibleSummaryPosition = COLLAPSIBLE_SUMMARY_POSITION.TOP,
  chevronContainerClassName,
  timeout = 'auto',
}: CollapsibleWrapperProps) => {
  const [isOpen, setIsOpen] = useState(defaultState);
  const onClick = () => {
    if (!isCollapsible) {
      return;
    }
    setIsOpen(!isOpen);
    onCollapse(!isOpen);
  };

  const getMode = (): ExpansionMode => (actionCard ? 'grow' : expansionMode);

  const isExpanded = (open ?? isOpen) && !disabled;

  const chevronContainer =
    disabledWithoutChevron && disabled ? null : (
      <ChevronContainer
        isCollapsible={isCollapsible}
        isExpanded={isExpanded}
        label={typeof title === 'string' && title ? title : TOGGLE_FALLBACK_LABEL}
        disabled={disabled}
        onClick={onClick}
        className={cn(
          styles.chevronContainer,
          // This keeps the layout fairly similar to what it looked like before changing the chevron
          // to be inside a keyboard accessible button. The bottom padding mainly affects consumers for whom
          // the chevron is rendered on top of the title.
          { 'pb-[6px]': chevronSide === CHEVRON_SIDE.LEFT },
          'pr-12',
          chevronContainerClassName
        )}
      />
    );

  const summarySection = (
    <div
      className={cn(
        styles.container,
        {
          [styles.actionCardContainer]: actionCard,
          [styles.noBottomBorder]: noDivider && actionCard,
          [styles.cursorDefault]: openOnChevronClickOnly,
        },
        styles[variantClassNames.container[variant]]
      )}
      onClick={openOnChevronClickOnly ? noop : onClick}
    >
      <div className={cn(styles.summarySectionContainer, summarySectionClassname, 'w-full')}>
        {chevronSide === CHEVRON_SIDE.LEFT && chevronContainer}
        <div className={styles.titleSectionContainer}>
          <div className={styles.titleContainer}>
            <Typography
              className={cn(
                styles.title,
                styles[variantClassNames.title[variant]],
                styles[titleColorClassNames[titleColor]],
                {
                  [styles.actionCardTitle]: actionCard,
                  [styles.disabled]: disabled,
                },
                // Only when the title is plain text — a ReactNode title would push the break
                // behaviour onto every descendant of consumer-supplied JSX.
                isPlainText(title) && 'wrap-anywhere'
              )}
              display="block"
              variant="h6"
            >
              {title}
            </Typography>
            {titleTag}
          </div>
          <Typography
            className={cn(
              styles.subtitle,
              styles[variantClassNames.subTitle[variant]],
              {
                [styles.actionCardSubtitle]: actionCard,
                hidden: !subtitle && !additionalSubtitleOnlyWhenOpen,
              },
              // A subtitle can be one unbreakable token that would otherwise overflow the header.
              // `anywhere`, not `break-words`: only `anywhere` lowers this flex item's min-content
              // width. Both slots this Typography renders are checked, since either one being a node
              // would put consumer JSX under the wrap.
              isPlainText(subtitle) && isPlainText(additionalSubtitleOnlyWhenOpen) && 'wrap-anywhere'
            )}
            display="block"
            variant="subtitle1"
          >
            {subtitle}
            {isOpen && additionalSubtitleOnlyWhenOpen ? <div>{additionalSubtitleOnlyWhenOpen}</div> : null}
          </Typography>
          {additionalHeaderContent}
        </div>
        {actions && (hideActionsOnCollapse ? isOpen : true) && (
          <div
            className={cn(styles.actionsContainer, {
              ['w-full']: withActionsContainerFullWidth,
            })}
          >
            {actions}
          </div>
        )}
      </div>
      {chevronSide === CHEVRON_SIDE.RIGHT && chevronContainer}
    </div>
  );

  return (
    <>
      <div
        className={cn(
          styles.collapsibleWrapper,
          backgroundColor ? styles[backgroundColorClassNames[backgroundColor]] : undefined,
          styles[borderColorClassNames[borderColor]],
          'flex flex-col',
          {
            [styles.collapsibleActionCard]: actionCard,
            [styles.cardShadow]: isCardShadow,
            [styles.noBorder]: noBorder,
            [styles.noBackground]: noBackground,
            [styles.noHorizontalPadding]: noHorizontalPadding,
            [styles.noBorderLine]: noBorderLine,
            [cn('flex w-full items-center justify-between')]:
              chevronSide === CHEVRON_SIDE.RIGHT && collapsibleSummaryPosition === COLLAPSIBLE_SUMMARY_POSITION.TOP,
            'p-0': disabledWithoutPadding && disabled,
            'flex justify-center': centerCollapsibleSummary,
          },
          className
        )}
      >
        {collapsibleSummaryPosition === COLLAPSIBLE_SUMMARY_POSITION.TOP && summarySection}

        {getMode() === 'grow' ? (
          <Collapse
            className={isActionCardDrawer ? styles.actionCardDrawer : undefined}
            in={isExpanded}
            timeout={timeout}
            unmountOnExit={unmountOnExit}
          >
            {children}
          </Collapse>
        ) : null}
        {getMode() === 'drawer' && (
          <Collapse in={isExpanded} timeout={timeout} unmountOnExit>
            {children}
          </Collapse>
        )}
        {collapsibleSummaryPosition === COLLAPSIBLE_SUMMARY_POSITION.BOTTOM && summarySection}
      </div>
    </>
  );
};

// Static properties for backward compatibility
CollapsibleWrapper.VARIANT = VARIANT;
CollapsibleWrapper.TITLE_COLOR = TITLE_COLOR;
CollapsibleWrapper.EXPANSION_MODES = EXPANSION_MODES;
CollapsibleWrapper.BORDER_COLOR = BORDER_COLOR;
CollapsibleWrapper.BACKGROUND_COLOR = BACKGROUND_COLOR;
CollapsibleWrapper.CHEVRON_SIDE = CHEVRON_SIDE;
CollapsibleWrapper.COLLAPSIBLE_SUMMARY_POSITION = COLLAPSIBLE_SUMMARY_POSITION;

export default CollapsibleWrapper;
